import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useUser } from '../../context/UserContext';
import { recapturaAprobaciones, recapturaDecision } from '../../api/apiConfig';
import { fmtMM, pillClass, unwrap } from './recapturaUtils';

const RecapturaAprobaciones = () => {
  const { user } = useUser();
  const [pendientes, setPendientes] = useState([]);
  const [resueltas, setResueltas] = useState([]);
  const [obs, setObs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = unwrap(await recapturaAprobaciones(user));
      if (resp.ok === false) throw new Error(resp.error || 'Error');
      setPendientes(resp.pendientes || []);
      setResueltas(resp.resueltas || []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar aprobaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const decide = async (propuestaId, resultado) => {
    const fundamento = (obs[propuestaId] || '').trim();
    if (!fundamento) {
      window.alert('Registra el fundamento de la resolución.');
      return;
    }
    try {
      const resp = unwrap(
        await recapturaDecision(user, {
          propuesta_id: propuestaId,
          resultado,
          fundamento,
        }),
      );
      if (!resp.ok) throw new Error(resp.error || 'Error al resolver');
      await load();
    } catch (e) {
      window.alert(e.message || 'No se pudo resolver');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600">
        <AiOutlineLoading3Quarters className="animate-spin" /> Cargando bandeja…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Activación Comercial — Aprobaciones</h1>
        <p className="text-sm text-gray-600">Fundamento obligatorio. La aprobación no sustituye evaluación de riesgo ni cesión real.</p>
      </div>

      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      {pendientes.length === 0 && !error && (
        <div className="seccion-datos text-gray-600">No hay propuestas pendientes.</div>
      )}

      {pendientes.map((p) => (
        <div key={p.propuesta_id} className="seccion-datos space-y-3">
          <div className="flex flex-wrap justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">{p.cliente || p.rut}</h2>
              <div className="text-xs text-gray-500">{p.ejecutivo} · {p.categoria}</div>
            </div>
            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold h-fit ${pillClass(p.etapa || p.estado)}`}>
              {p.etapa || p.estado}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <p><b>Motivo:</b> {p.motivo_mgc || '—'}</p>
              <p><b>Competencia:</b> {p.competidor || '—'}</p>
              <p><b>Condición solicitada:</b> {p.propuesta || '—'}</p>
              <p><b>Beneficio:</b> {p.beneficio || '—'}</p>
            </div>
            <div>
              <p><b>Cesiones externas:</b> {fmtMM((p.cesiones_banco || 0) + (p.cesiones_otros || 0))}</p>
              <p><b>Volumen recuperable:</b> {fmtMM(p.volumen_recuperable)}</p>
              <p><b>Compromiso:</b> {p.compromiso || '—'}</p>
            </div>
          </div>
          <label className="block text-xs font-semibold text-gray-600">Observación / condición gerencial</label>
          <textarea
            className="w-full px-3 py-2 border rounded-lg text-sm"
            rows={2}
            value={obs[p.propuesta_id] || ''}
            onChange={(e) => setObs((prev) => ({ ...prev, [p.propuesta_id]: e.target.value }))}
            placeholder="Fundamento, vigencia o condición de aprobación"
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-primary text-sm" onClick={() => decide(p.propuesta_id, 'Aprobado')}>Aprobar</button>
            <button type="button" className="text-sm py-2 px-3 border rounded-lg bg-white" onClick={() => decide(p.propuesta_id, 'Ajustes solicitados')}>Solicitar ajustes</button>
            <button type="button" className="text-sm py-2 px-3 border rounded-lg bg-white text-red-700" onClick={() => decide(p.propuesta_id, 'Rechazado')}>Rechazar</button>
            <Link
              to={`/recaptura/ficha/${encodeURIComponent(p.rut)}`}
              state={{ clienteId: p.cliente_id || p.id }}
              className="text-sm py-2 px-3 border rounded-lg"
              style={{ textDecoration: 'none' }}
            >
              Ver ficha completa
            </Link>
          </div>
        </div>
      ))}

      <div className="seccion-datos">
        <h2 className="font-semibold text-gray-800 mb-3">Resoluciones anteriores</h2>
        {(resueltas || []).length === 0 && <p className="text-sm text-gray-500">Sin resoluciones aún.</p>}
        {(resueltas || []).map((r, i) => (
          <p key={`${r.propuesta_id}-${i}`} className="text-sm mb-2">
            <b>{r.cliente || r.rut}</b>{' '}
            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${pillClass(r.resultado || r.estado)}`}>
              {r.resultado || r.estado}
            </span>
            {' · '}{r.fundamento || r.propuesta || ''}
          </p>
        ))}
      </div>
    </div>
  );
};

export default RecapturaAprobaciones;
